/** Outbound HTTP fetch -- the ONE standardized fetch tool. `httpFetch` is the single transport primitive: an options
 * object carrying the retired variants' real deltas (timeout, retry/backoff, 401 auth-retry), returning a discriminated
 * union that never throws on transport/timeout failure; `httpFetchOrThrow` is the throwing convenience returning a bare
 * `Response`. Composition root: the attempt/retry/auth engines live in the `utils/http/fetch-*` leaves (which cannot
 * value-import their utils-sibling helpers, so every seam is injected here). */

import { API_TIMEOUTS } from '@federa/const/timing';
import { checkResponseOk } from '@federa/const/http-response-check';
import { type HttpClientErrorCode } from '@federa/const/http-error-codes';
import { delay } from '@federa/utils/async/delay';
import { UPSTREAM_ERROR_BODY_PREVIEW_MAX_CHARS } from '@federa/const/observability-error-preview';
import { BaseError } from '@federa/const/base-error';
import { ERROR_CODES } from '@federa/const/error-codes';
import { httpErrorContextDetails, httpErrorContextPrefix } from '@federa/const/http-error-context';
import { TRANSPORT_FAILURE_ERROR_READERS, getErrorMessage, normalizeError } from '@federa/utils/errors/error-core';
import { classifyTransportFailure } from '@federa/utils/http/transport-failure-classify';
import { isRetryableError } from '@federa/utils/http/retry-config';
import { setupAbortController } from '@federa/utils/http/fetch-abort';
import { handleTimeoutError } from '@federa/utils/http/handle-timeout-error';
import { makeFetchAttempt } from '@federa/utils/http/fetch-attempt-core';
import { makeRetryTransport } from '@federa/utils/http/fetch-retry-loop';
import { makeSingleAttempt } from '@federa/utils/http/fetch-single-attempt';
import type { AuthRetryConfig, FetchJsonConfig, FetchJsonSafeConfig } from '@federa/types/http';
import type { FetchJsonSafeResult, HttpRetryConfig } from '@federa/types/http-api';

/** Options for {@link httpFetch}. All optional: absent = one plain attempt, no timeout, no retry. */
interface HttpFetchOptions {
	/** Standard RequestInit (method/headers/body/...). */
	init?: RequestInit;
	/** Race the attempt against a timeout of this many ms. Absent = no race. */
	timeoutMs?: number;
	/** Retry transient failures (5xx/429/network) with backoff + Retry-After. */
	retry?: Partial<HttpRetryConfig>;
	/** When set, seed a bearer token and refresh-and-retry once on a 401. */
	authRetry?: AuthRetryConfig;
	/** External abort signal, composed with any timeout controller. */
	signal?: AbortSignal;
}

/** Result of {@link httpFetch}: a Response, or a classified transport failure. */
type HttpFetchResult = { ok: true; response: Response } | { ok: false; error: string; code: HttpClientErrorCode };

/**
 * The bound engines: one attempt executor feeding the retry loop, plus the plain/auth single-attempt arms.
 *
 * THE TWO ENGINES SPELL THE SAME RACED-FETCH BLOCK, AND THE FOLD IS DECLINED. `makeFetchAttempt`
 * (`utils/http/fetch-attempt-core.ts`) and `makeSingleAttempt` (`utils/http/fetch-single-attempt.ts`) each open a
 * `setupAbortController`, race the `fetch` promise against the timeout promise, hand a throw to `handleTimeoutError`,
 * and `cleanup()` in a `finally`. The adjudication lives HERE, on the symbols both leaves orbit, rather than in each
 * leaf's own docstring, because two copies of one rationale is the waste
 * `QUAL-CROSS-FILE-COMMENT-PROSE-RUN-DUP-001` names -- each leaf carries a pointer to this note instead.
 *
 * Two independent axes foreclose it. (1) LAYER FLOOR: a shared raced-fetch helper would have to be a third `utils/`
 * leaf, and `QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001` bans utils-to-utils value imports as HARD operator policy with no
 * allowlist escape -- so the only landing shape is yet another seam injected from this file, which trades one spelling
 * for a wider DI surface and leaves the block itself intact. (2) RETURN CONTRACT: the attempt executor resolves to a
 * classified `{ response | error, shouldRetry }` envelope and swallows the throw so the retry loop can read it, while
 * the single-attempt arm THROWS and additionally short-circuits to a bare `fetch` when no timeout is set. A shared
 * helper would have to rethrow, which moves `cleanup()` ahead of the classification instead of after it -- a reordering
 * no reading of the source proves inert. Re-propose only against an operator-adjudicated change to the utils import
 * floor.
 */
const runWithRetry = makeRetryTransport({
	attemptFn: makeFetchAttempt({
		setupAbortController,
		handleTimeoutError,
		isRetryableError: (error, config) => isRetryableError(error as Parameters<typeof isRetryableError>[0], config),
	}),
	delay,
	normalizeError,
	makeFetchFailedError: () => new BaseError({ code: ERROR_CODES.HTTP_FETCH_FAILED, message: 'Fetch failed with unknown error' }),
});
const { singleAttempt, authRetryAttempt } = makeSingleAttempt({ setupAbortController, handleTimeoutError });

function dispatch(url: string | URL | Request, options: HttpFetchOptions): Promise<Response> {
	const { init, timeoutMs, retry, authRetry, signal } = options;
	const mergedInit: RequestInit | undefined = signal ? { ...init, signal } : init;
	if (retry) return runWithRetry(url, mergedInit ?? {}, retry);
	if (authRetry) {
		const headers = new Headers(mergedInit?.headers);
		return authRetryAttempt({ url, init: mergedInit, headers, timeoutMs, authRetry, attempt: 0, maxAttempts: authRetry.maxAttempts ?? 2 });
	}
	return singleAttempt(url, mergedInit, timeoutMs);
}

/** Never throws on transport/timeout failure -- returns `{ ok: false, error, code }` (TIMEOUT / NETWORK_ERROR / UNKNOWN_ERROR); any HTTP response returns `{ ok: true, response }`. */
export async function httpFetch(url: string | URL | Request, options: HttpFetchOptions = {}): Promise<HttpFetchResult> {
	try {
		return { ok: true, response: await dispatch(url, options) };
	} catch (error) {
		// The three-way verdict is shared with the app tier's service-binding client, which dials a Fetcher binding
		// this dispatcher cannot reach -- hence one classifier, two diallers (utils/http/transport-failure-classify.ts).
		// The two error readers are bound HERE, through their one bundle, because that utils leaf may not import a utils sibling.
		return { ok: false, ...classifyTransportFailure(error, TRANSPORT_FAILURE_ERROR_READERS) };
	}
}

/** Throwing convenience over {@link httpFetch}: the `Response` (any status), or `BaseError(HTTP_FETCH_FAILED)` with an optional `context` prefix on transport/timeout failure. */
export async function httpFetchOrThrow(url: string | URL | Request, options: HttpFetchOptions = {}, context?: string): Promise<Response> {
	const result = await httpFetch(url, options);
	if (result.ok) return result.response;
	throw new BaseError({
		code: ERROR_CODES.HTTP_FETCH_FAILED,
		message: `${httpErrorContextPrefix(context)}${result.error}`,
		details: httpErrorContextDetails(context),
	});
}

// Typed JSON layer. `fetchJson` rides `runWithRetry` directly rather than `httpFetchOrThrow({ retry })` -- migrating it
// changes its transport-failure throw type, a MONEY-PATH nuance needing payments catch-path sign-off (deferred).
const FETCH_JSON_DEFAULTS: Partial<HttpRetryConfig> = { maxRetries: 0, timeout: API_TIMEOUTS.standard };

/** Fetch URL, assert ok, parse JSON (default 10s timeout, no retries). */
export async function fetchJson<T = Record<string, unknown>>(
	url: string,
	options: RequestInit,
	{ errorContext, retry: retryConfig = {}, validate }: FetchJsonConfig<T>,
): Promise<T> {
	const config = { ...FETCH_JSON_DEFAULTS, ...retryConfig };
	const response = await runWithRetry(url, options, config);
	const httpCheck = await checkResponseOk(response);
	if (!httpCheck.ok) {
		const msg = `${errorContext} error: ${httpCheck.status} - ${httpCheck.body ?? ''}`;
		throw new BaseError({ code: ERROR_CODES.HTTP_FETCH_FAILED, message: msg, details: { errorContext, status: httpCheck.status } });
	}
	let rawResult: unknown;
	try {
		rawResult = await response.json();
	} catch (parseError) {
		const msg = `${errorContext} JSON parse error: ${getErrorMessage(parseError)}`;
		throw new BaseError({ code: ERROR_CODES.HTTP_JSON_PARSE_FAILED, message: msg, cause: parseError, details: { errorContext } });
	}
	if (validate && !validate(rawResult)) {
		throw new BaseError({
			code: ERROR_CODES.HTTP_JSON_PARSE_FAILED,
			message: `${errorContext} response shape did not match validator`,
			details: { errorContext },
		});
	}
	return rawResult as T;
}

/** Unlike `fetchJson`, this NEVER throws -- failures ride the returned union. */
export async function fetchJsonSafe<T = Record<string, unknown>>(
	url: string | URL,
	init: RequestInit,
	{ errorContext, validate }: FetchJsonSafeConfig<T>,
): Promise<FetchJsonSafeResult<T>> {
	const result = await httpFetch(url, { init });
	if (!result.ok) return { ok: false, error: `${errorContext}: ${result.error}`, status: 0 };
	const { response } = result;
	const check = await checkResponseOk(response);
	if (!check.ok)
		return {
			ok: false,
			error: `${errorContext}: HTTP ${check.status} - ${(check.body ?? '').slice(0, UPSTREAM_ERROR_BODY_PREVIEW_MAX_CHARS)}`,
			status: check.status,
		};
	try {
		const raw: unknown = await response.json();
		if (validate && !validate(raw)) {
			return { ok: false, error: `${errorContext}: response shape did not match validator`, status: response.status };
		}
		const data = raw as T;
		return { ok: true, data, status: response.status };
	} catch (parseError) {
		return { ok: false, error: `${errorContext}: JSON parse error - ${getErrorMessage(parseError)}`, status: response.status };
	}
}
